import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { mangaList } from "@/mocks/manga";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "新番导视", to: "/guide" },
  { label: "连载新开", to: "/serial" },
  { label: "热门新番", to: "/hot" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [showResult, setShowResult] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResult(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = keyword.trim()
    ? mangaList.filter(
        (m) =>
          m.title.includes(keyword.trim()) ||
          m.tags.some((t) => t.includes(keyword.trim()))
      )
    : [];

  const goResult = (id: string) => {
    setKeyword("");
    setShowResult(false);
    navigate(`/article/${id}`);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 shadow-soft backdrop-blur"
          : "bg-background-50"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        {/* logo */}
        <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="番木瓜漫画 首页">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-background-50">
            <i className="ri-leaf-fill text-xl"></i>
          </span>
          <span className="font-heading text-xl text-primary-700 md:text-2xl">
            番木瓜漫画
          </span>
        </Link>

        {/* desktop nav */}
        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="rounded-lg px-3 py-2 text-sm font-medium text-foreground-700 transition hover:bg-primary-100 hover:text-primary-800 whitespace-nowrap"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* search */}
        <div ref={searchRef} className="relative hidden lg:block">
          <div className="flex items-center rounded-full border border-background-300 bg-background-50 px-3 py-1.5">
            <i className="ri-search-line mr-2 text-sm text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setShowResult(true);
              }}
              onFocus={() => setShowResult(true)}
              placeholder="搜索新番 / 标签"
              className="w-44 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              aria-label="搜索"
            />
          </div>
          {showResult && keyword.trim() && (
            <div className="absolute right-0 top-11 w-72 overflow-hidden rounded-xl border border-background-200 bg-background-50 shadow-soft">
              {results.length === 0 ? (
                <p className="px-4 py-3 text-sm text-foreground-500">
                  没有找到相关作品
                </p>
              ) : (
                <ul>
                  {results.map((m) => (
                    <li key={m.id}>
                      <button
                        onClick={() => goResult(m.id)}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-primary-100"
                      >
                        <img
                          src={m.cover}
                          alt={m.title}
                          className="h-10 w-8 rounded object-cover object-top"
                        />
                        <span className="flex-1">
                          <span className="block text-sm font-medium text-foreground-900">
                            {m.title}
                          </span>
                          <span className="block text-xs text-foreground-500">
                            {m.tags.join(" · ")}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* hamburger */}
        <button
          onClick={() => setOpen(!open)}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-800 md:hidden"
          aria-label="打开菜单"
        >
          <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
        </button>
      </div>

      {/* mobile menu */}
      {open && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-800 transition hover:bg-primary-100"
              >
                {l.label}
              </Link>
            ))}
            <div className="mt-1 flex items-center rounded-full border border-background-300 px-3 py-2">
              <i className="ri-search-line mr-2 text-sm text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索新番 / 标签"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                aria-label="搜索"
              />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}